import { isEqual } from 'lodash';
import type { Ref } from 'react';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OdinKey } from '../../../common/types/odin-key.type';
import type { ModalDialogButtonSetting } from '../../common-interfaces';
import type { DynamicFieldDataProps } from '../../dynamic-form/dynamic-form.interfaces';
import type { DataSearchLocation } from '../../enums';
import type { DropDownResult } from '../../fields/drop-down/drop-down-types';
import type { TwinColumnSelectRef } from '../../fields/twin-column-select/twin-column-select.component';
import TwinColumnSelect from '../../fields/twin-column-select/twin-column-select.component';
import type { DialogProps, DialogRef } from '../../modal-dialog/dialog.component';
import Dialog from '../../modal-dialog/dialog.component';

export interface TwinColumnSelectModalProps
  extends Omit<DialogProps, 'children' | 'buttons' | 'id'>,
    DynamicFieldDataProps {
  id?: OdinKey;
  value?: Array<DropDownResult>;
  addValue: (value?: Array<DropDownResult>) => void;
  subType?: string;
  filterLocation?: DataSearchLocation;
}

function TwinColumnSelectModal(props: TwinColumnSelectModalProps, ref: Ref<DialogRef>) {
  const modalProps: Omit<DialogProps, 'children' | 'buttons'> = {
    ...props,
    id: props.id ? `${props.id}` : undefined,
  };
  const {
    id,
    value = [],
    data,
    textField,
    valueField,
    pageSize,
    addValue,
    showAddNewOption,
    subType,
    filterLocation,
  } = props;

  const { t } = useTranslation();

  const [stateValue, setStateValue] = useState(value ?? []);
  const [applyButton, setApplyButton] = useState<ModalDialogButtonSetting>({
    name: 'apply',
    text: t('apply', 'Apply'),
    type: 'primary',
    variant: 'alternative',
    closeModalOnClick: true,
    alignment: 'right',
    disabled: true,
    onClick: () => {
      const value = twinColumnRef.current?.retrieveValues();
      if (value) {
        addValue(value);
      }

      setApplyButton({ ...applyButton, disabled: true });
    },
  });

  const twinColumnRef = useRef<TwinColumnSelectRef>(null);

  const clearButton = useRef<ModalDialogButtonSetting>({
    name: 'clear',
    text: t('clear', 'Clear'),
    variant: 'alternative',
    alignment: 'right',
    classNames: 'focus:ring-primary-2',
    onClick: () => twinColumnRef.current?.clearData(),
  });

  const advancedButton = useRef<ModalDialogButtonSetting>({
    name: 'advanced',
    text: t('advanced', 'Advanced'),
    variant: 'alternative',
    closeModalOnClick: false,
    alignment: 'left',
    classNames: 'focus:ring-primary-2 hidden', //Hiding button until feature is done
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentionally only sync when prop value changes, not when stateValue changes
  useEffect(() => {
    if (!isEqual(value, stateValue)) {
      setStateValue(value);
    }
  }, [value]);

  const onClose = useCallback(() => {
    props.onClose?.();

    twinColumnRef.current?.clearData();

    //Clicking outside resets apply button's state
    setApplyButton({ ...applyButton, disabled: true });
  }, [props, applyButton, twinColumnRef]);

  const onChangeHandler = (newSelection: Array<DropDownResult> = []) => {
    const oldValueOrEmpty: Array<DropDownResult> = value ?? [];
    const isSameValue =
      newSelection.length === oldValueOrEmpty.length &&
      isEqual(
        newSelection.flatMap((item) => {
          return { text: item.text, value: item.value };
        }),
        oldValueOrEmpty.flatMap((item) => {
          return { text: item.text, value: item.value };
        })
      );

    // Updating value resets Apply button's state
    setApplyButton((state) => ({ ...state, disabled: isSameValue }));
  };

  return (
    <>
      {modalProps.visible && (
        <Dialog
          {...modalProps}
          ref={ref}
          buttons={[advancedButton.current, clearButton.current, applyButton]}
          onClose={onClose}
          containerClassName="md:min-w-[700px]"
        >
          <TwinColumnSelect
            ref={twinColumnRef}
            id={id}
            data={data ?? []}
            value={stateValue}
            textField={textField}
            valueField={valueField}
            pageSize={pageSize}
            onChange={onChangeHandler}
            showAddNewOption={showAddNewOption}
            subType={subType}
            filterLocation={filterLocation}
          />
        </Dialog>
      )}
    </>
  );
}

export default forwardRef(TwinColumnSelectModal);
